import React, { useEffect, useMemo, useRef, useState } from "react";
import { useWalletKit, ConnectButton } from "@mysten/wallet-kit";
import { useTheme } from "./ThemeContext";
import "./EnhancedNavbar.css";

interface EnhancedNavbarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  user?: any;
  onUserLogin?: () => void;
  onUserLogout?: () => void;
  onProfileUpdate?: (profileData: any) => void;
}

type SidebarButton = {
  id: "home" | "create" | "swap";
  label: string;
  icon: React.ReactNode;
  path: string;
  value: "home" | "create" | "swap";
  accent?: "neutral" | "primary";
  variant?: "default" | "cta";
};

const SidebarHomeIcon: React.FC = () => (
  <svg
    className="sidebar-icon-svg"
    viewBox="0 0 24 24"
    role="img"
    aria-hidden="true"
  >
    <path
      d="M4.5 10.5L12 4l7.5 6.5"
      strokeWidth={1.6}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M7 10v9h4.5v-5h3v5H19v-9"
      strokeWidth={1.6}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M10.75 14.5h2.5"
      strokeWidth={1.6}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const SidebarCreateIcon: React.FC = () => (
  <svg
    className="sidebar-icon-svg"
    viewBox="0 0 24 24"
    role="img"
    aria-hidden="true"
  >
    <circle
      cx={12}
      cy={12}
      r={7}
      strokeWidth={1.6}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M12 8.5v7"
      strokeWidth={1.8}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M8.5 12h7"
      strokeWidth={1.8}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const SidebarSupportIcon: React.FC = () => (
  <svg
    className="sidebar-icon-svg"
    viewBox="0 0 24 24"
    role="img"
    aria-hidden="true"
  >
    <circle
      cx={12}
      cy={12}
      r={8}
      strokeWidth={1.6}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"
      strokeWidth={1.6}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <circle
      cx={12}
      cy={17}
      r={0.5}
      fill="currentColor"
    />
  </svg>
);

const EnhancedNavbar: React.FC<EnhancedNavbarProps> = ({
  currentPage,
  onNavigate,
  sidebarCollapsed,
  onToggleSidebar,
  user,
  onUserLogin,
  onUserLogout,
  onProfileUpdate,
}) => {
  const { isDarkMode: prefersDark, toggleTheme } = useTheme();
  const { isConnected, currentAccount, disconnect } = useWalletKit();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [notifications, setNotifications] = useState(() => [
    { id: Date.now(), message: "New token PEPE launched!", time: "2m ago", type: "launch" as const },
    { id: Date.now() - 1000, message: "Your trade was successful", time: "5m ago", type: "trade" as const },
    { id: Date.now() - 2000, message: "DOGE reached new high!", time: "10m ago", type: "alert" as const }
  ]);
  const actionsRef = useRef<HTMLDivElement>(null);
  const walletMenuRef = useRef<HTMLDivElement>(null);
  const [actionsWidth, setActionsWidth] = useState(0);
  const [showWalletMenu, setShowWalletMenu] = useState(false);
  const [slippage, setSlippage] = useState(5);

  useEffect(() => {
    const element = actionsRef.current;
    if (!element) return;

    const updateWidth = () => {
      if (actionsRef.current) {
        setActionsWidth(Math.ceil(actionsRef.current.getBoundingClientRect().width));
      }
    };

    updateWidth();

    const resizeObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(updateWidth)
      : null;

    if (resizeObserver) {
      resizeObserver.observe(element);
    } else {
      window.addEventListener("resize", updateWidth);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", updateWidth);
      }
    };
  }, []);

  useEffect(() => {
    const phrases = [
      { message: "Liquidity milestone reached on MEOW token", type: "launch" as const },
      { message: "Whale bought 42K SUI of FLOOF", type: "trade" as const },
      { message: "🚀 Bonding curve moving to Stage 2", type: "alert" as const },
      { message: "Your watchlist token LFG minted 1K holders", type: "launch" as const },
      { message: "Gas fees optimized for your next deploy", type: "alert" as const }
    ];

    const interval = window.setInterval(() => {
      const phrase = phrases[Math.floor(Math.random() * phrases.length)];
      const now = new Date();
      const formattedTime = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      setNotifications((prev) => {
        const next = [
          {
            id: now.getTime(),
            message: phrase.message,
            time: formattedTime,
            type: phrase.type
          },
          ...prev
        ];
        return next.slice(0, 6);
      });
    }, 45000);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowNotifications(false);
        setShowWalletMenu(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (walletMenuRef.current && !walletMenuRef.current.contains(event.target as Node)) {
        setShowWalletMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isConnected) {
      setShowWalletMenu(false);
    }
  }, [isConnected]);

  const handleWalletToggle = () => {
    if (!isConnected) return;
    setShowWalletMenu((prev) => !prev);
  };

  const handleWalletDisconnect = async () => {
    try {
      await disconnect();
    } catch (error) {
      console.error("Failed to disconnect wallet", error);
    } finally {
      setShowWalletMenu(false);
    }
  };

  const handleNavigate = (path: string) => {
    onNavigate(path);
  };

  const sidebarItems: SidebarButton[] = useMemo(
    () => [
      {
        id: "home",
        label: "Home",
        icon: <SidebarHomeIcon />,
        path: "/home",
        value: "home",
        accent: "neutral",
      },

      {
        id: "create",
        label: "Create Coin",
        icon: <SidebarCreateIcon />,
        path: "/create",
        value: "create",
        accent: "primary",
        variant: "cta",
      },
    ],
    []
  );

  const formattedWallet = useMemo(() => {
    if (!currentAccount?.address) return null;
    const addr = currentAccount.address;
    return `${addr.slice(0, 4)}...${addr.slice(-4)}`;
  }, [currentAccount]);

  return (
    <>
      <aside className={`enhanced-sidebar ${sidebarCollapsed ? "collapsed" : ""}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand" style={{ display: 'none' }}>
            {/* Icon removed per user request */}
          </div>
          <button
            className={`sidebar-toggle ${sidebarCollapsed ? "collapsed" : "expanded"}`}
            onClick={onToggleSidebar}
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            type="button"
          >
            <span className="toggle-glow" aria-hidden="true" />
            <span className="toggle-icon" aria-hidden="true">
              <svg className="toggle-chevron" viewBox="0 0 24 24" role="presentation">
                <defs>
                  <linearGradient id="sidebarToggleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#38bdf8" />
                    <stop offset="50%" stopColor="#34d399" />
                    <stop offset="100%" stopColor="#f87171" />
                  </linearGradient>
                </defs>
                <path
                  className="toggle-chevron-path"
                  d="M9 6l6 6-6 6"
                  fill="none"
                  stroke="url(#sidebarToggleGradient)"
                  strokeWidth={2.4}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="toggle-ripple" aria-hidden="true" />
          </button>
        </div>

        <nav className="sidebar-nav">
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              data-item={item.id}
              data-accent={item.accent ?? "neutral"}
              data-variant={item.variant ?? "default"}
              className={`sidebar-nav-button ${currentPage === item.value ? "active" : ""}`}
              onClick={() => handleNavigate(item.path)}
              type="button"
            >
              <span className="sidebar-nav-icon" aria-hidden="true">{item.icon}</span>
              <span className="sidebar-nav-label">{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button
            className="sidebar-support-button"
            onClick={() => window.open('https://t.me/yoursupportchannel', '_blank')}
            type="button"
            title="Get support"
          >
            <span className="sidebar-nav-icon" aria-hidden="true">
              <SidebarSupportIcon />
            </span>
            <span className="sidebar-nav-label">Support</span>
          </button>
        </div>
      </aside>

      <header className="navbar">
        <div className="navbar-container">
          <div className="navbar-core-strip">
            <div className="navbar-left">
              <div className="navbar-brand">
                <div className="brand-copy">
                  <span className="brand-name">SUI MEMELAUNCHPAD</span>
                </div>
              </div>
            </div>

            <div className="navbar-center">
              <nav className="navbar-links" aria-label="Primary">
                <button
                  className={`nav-link ${currentPage === "home" ? "active" : ""}`}
                  onClick={() => handleNavigate("/home")}
                  type="button"
                  title="Browse live markets"
                >
                  Home
                </button>

                <button
                  className="nav-link"
                  onClick={() => handleNavigate("/discover")}
                  type="button"
                  title="Discover trending launches"
                >
                  Discover
                </button>
                <button
                  className={`nav-link ${currentPage === "create" ? "active" : ""}`}
                  onClick={() => handleNavigate("/create")}
                  type="button"
                  title="Launch a new meme token"
                >
                  Create Token
                </button>
                <button
                  className="nav-link"
                  onClick={() => handleNavigate("/leaderboard")}
                  type="button"
                  title="View top performers"
                >
                  Leaderboard
                </button>
              </nav>
            </div>

            <div className="navbar-right" ref={actionsRef}>
              <button
                className="header-secondary"
                onClick={() => handleNavigate("/how-it-works")}
                type="button"
                title="Understand the launch process"
              >
                How it works?
              </button>

              <button
                className="header-icon"
                onClick={toggleTheme}
                title={`Switch to ${prefersDark ? "Light" : "Dark"} theme`}
                type="button"
              >
                {prefersDark ? "🌙" : "☀️"}
              </button>

              {user ? (
                <div
                  className="profile-chip"
                  onClick={() => setShowProfileEdit(!showProfileEdit)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && setShowProfileEdit(!showProfileEdit)}
                  title="Open profile menu"
                >
                  <span className="chip-avatar">
                    {user.avatar ? (
                      <img src={user.avatar} alt="Profile" />
                    ) : (
                      <span>{user.username ? user.username[0].toUpperCase() : "?"}</span>
                    )}
                  </span>
                  <span className="chip-name">{user.username || "Launchpad User"}</span>
                  <span className="chip-arrow">▼</span>
                  {showProfileEdit && (
                    <div className="profile-dropdown premium" role="dialog">
                      <div className="profile-dropdown-header">
                        <div className="user-avatar-large">
                          {user.avatar ? (
                            <img src={user.avatar} alt="Profile" />
                          ) : (
                            <div className="user-avatar-placeholder">
                              {user.username ? user.username[0].toUpperCase() : "?"}
                            </div>
                          )}
                        </div>
                        <div className="user-details">
                          <h3>{user.username || "Launchpad User"}</h3>
                          <p>{user.wallet_address ? `${user.wallet_address.slice(0, 4)}...${user.wallet_address.slice(-4)}` : "No wallet"}</p>
                        </div>
                      </div>

                      <div className="profile-actions">
                        <button className="profile-action-btn edit-btn" onClick={() => onNavigate("/user-dashboard")}>✏️ Edit Profile</button>
                        <button className="profile-action-btn settings-btn" onClick={() => { }}>⚙️ Settings</button>
                        <button className="profile-action-btn disconnect-btn" onClick={onUserLogout}>📱 Disconnect</button>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              <div
                ref={walletMenuRef}
                className={`wallet-quick final ${isConnected ? "connected" : "disconnected"} ${showWalletMenu ? "expanded" : ""}`}
              >
                {isConnected && formattedWallet ? (
                  <>
                    <button
                      type="button"
                      className="wallet-menu-trigger"
                      onClick={handleWalletToggle}
                      aria-haspopup="menu"
                      aria-expanded={showWalletMenu}
                      title="Wallet actions"
                    >
                      <span className="wallet-menu-label">{formattedWallet}</span>
                      <span className="wallet-menu-arrow" aria-hidden="true">▾</span>
                    </button>
                    {showWalletMenu && (
                      <div className="wallet-quick-menu" role="menu">
                        <div className="wallet-menu-header">
                          <span className="wallet-menu-title">Connected wallet</span>
                          <span className="wallet-menu-address">{currentAccount?.address}</span>
                        </div>
                        <button
                          type="button"
                          className="wallet-menu-item"
                          onClick={handleWalletDisconnect}
                          role="menuitem"
                        >
                          <span className="wallet-menu-icon" aria-hidden="true" />
                          Disconnect
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <ConnectButton>
                    {formattedWallet ?? "Connect Wallet"}
                  </ConnectButton>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default EnhancedNavbar;
